<template>
  <ContentContainer :loadingStatus="isLoading">
    <div class="sale-person-list content-wrapper">
      <div class="page-header">{{ $t('salePerson') }}</div>
      <div>
        <el-row>
          <el-col :span="4">
            <el-input :placeholder="$t('keyword')"
                      v-model="keyword"
                      @input="isSearching = true"
            ></el-input>
          </el-col>
          <el-col :span="20" style="text-align: right;">
            <router-link to="sale-person/add" style="float: right">
              <el-button type="primary" icon="el-icon-plus"> {{ $t('newSalePerson') }}</el-button>
            </router-link>
          </el-col>
        </el-row>
        <div style="margin-top: 25px">
          <el-table
            id="sale-person-list-table"
            :data="salePersonListDataSource"
            v-loading="isSearching"
            header-row-class-name="table-header-color"
            style="width: 100%">
            <el-table-column
              :label="$t('fullName')">
              <template slot-scope="scope">{{ bindFullName(scope.row) }}</template>
            </el-table-column>
            <el-table-column
              :label="$t('position')">
              <template slot-scope="scope">{{ bindSalePersonDataByKey(scope.row, 'position') }}</template>
            </el-table-column>
            <el-table-column
              :label="$t('phone')">
              <template slot-scope="scope">{{ bindSalePersonDataByKey(scope.row, 'phone') }}</template>
            </el-table-column>
            <el-table-column
              prop="mobile"
              :label="$t('mobile')">
            </el-table-column>
            <el-table-column
              :label="$t('fax')">
              <template slot-scope="scope">{{ bindSalePersonDataByKey(scope.row, 'fax') }}</template>
            </el-table-column>
            <el-table-column
              prop="email"
              :label="$t('email')">
            </el-table-column>
            <el-table-column
              :label="$t('signature')">
              <template slot-scope="scope">
                <img v-if="scope.row.signatureUrl !== undefined && scope.row.signatureUrl !== ''" height="80px" alt="Sale Person Signature" :src="scope.row.signatureUrl">
              </template>
            </el-table-column>
            <el-table-column align="center">
              <template slot-scope="scope">
                <el-button type="primary" size="small" icon="el-icon-edit" plain circle @click="onEditButtonClick(scope.row)"></el-button>
                <el-button type="danger" size="small" icon="el-icon-delete" plain circle @click="onRemoveButtonClick(scope.row)"></el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
    </div>
  </ContentContainer>
</template>

<script>
import i18nMessages from '@/translations/Setting/ManageSetting/SalePerson/List.i18n'
import AlertViewMixin from '@/mixins/alert-view-mixin'
import LoadingViewMixin from '@/mixins/loading-view-mixin'
import NotificationMixin from '@/mixins/notification-mixin'
import SettingSalePersonApi from '@/api/setting/sale-person-api'
import ContentContainer from '@/components/ContentContainerComponent.vue'
import { filterDataByKeyword } from '@/services/general-service'
import { get, debounce } from 'lodash'

export default {
  name: 'SettingSalePersonList',
  mixins: [AlertViewMixin, LoadingViewMixin, NotificationMixin],
  components: { ContentContainer },
  i18n: { messages: i18nMessages },
  watch: {
    keyword: debounce(function () {
      this.isSearching = false
    }, 300),
    isSearching: function (value) {
      if (!value) {
        this.getSalePersonListDataSource()
      }
    }
  },
  data () {
    return {
      isLoading: true,
      keyword: '',
      isSearching: false,
      salePersonList: [],
      salePersonListDataSource: []
    }
  },
  created: async function () {
    try {
      this.isLoading = true
      this.salePersonList = await SettingSalePersonApi.list()

      this.salePersonList.sort((prev, next) => {
        return (prev.firstName < next.firstName) ? -1 : 1
      })
      this.getSalePersonListDataSource()
    } catch (err) {
      this.alertError(err.message)
    }

    this.isLoading = false
  },
  methods: {
    getSalePersonListDataSource: function () {
      const searchKeys = ['firstName', 'lastName']
      const filteredSalePersonList = filterDataByKeyword(this.salePersonList, this.keyword, searchKeys)
      this.salePersonListDataSource = [...filteredSalePersonList]
    },
    bindFullName: function (salePerson) {
      const title = get(salePerson, 'title', '')
      const fullName = `${title} ${salePerson.firstName} ${salePerson.lastName}`

      return fullName
    },
    bindSalePersonDataByKey: function (salePerson, key) {
      const salePersonData = get(salePerson, key, '')

      return (salePersonData === '') ? '-' : salePersonData
    },
    onEditButtonClick: function (salePerson) {
      this.$router.push({
        name: 'SettingSalePersonEdit',
        params: {
          salePerson: salePerson
        }
      })
    },
    onRemoveButtonClick: async function (row) {
      const confirmMessage = this.$t('confirmRemoveSalePersonMessage', {
        name: this.bindFullName(row)
      })
      const confirmButtonText = this.$t('confirmButtonText')
      const cancelButtonText = this.$t('cancelButtonText')
      const isConfirmRemove = await this.alertConfirmDelete(confirmMessage, confirmButtonText, cancelButtonText)

      if (!isConfirmRemove) {
        return false
      }

      this.removeSalePerson(row.id)
    },
    removeSalePerson: async function (salePersonId) {
      try {
        this.startLoading()

        await SettingSalePersonApi.remove(salePersonId)
        this.salePersonList = this.salePersonList.filter(item => item.id !== salePersonId)
        this.getSalePersonListDataSource()

        const notificationTitle = this.$t('removeSalePersonSuccess')
        this.showNotificationSuccess(notificationTitle)
      } catch (err) {
        this.alertError(err.message)
      }

      this.stopLoading()
    }
  }
}
</script>

<style scoped>
  #sale-person-list-table {
    width: 100%;
    border: 1px solid #dddddd;
    border-radius: 5px;
    box-shadow: 0px 3px 5px 1px #eeeeee;
  }
  .el-table th {
    background-color: #eeeeee;
  }
  .el-form > div {
    text-align: left;
  }
</style>
